import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TeamAvatar } from "@/components/common";
import { Shuffle, Save, GripVertical, Users2 } from "lucide-react";

const LABELS = "ABCDEFGH".split("");

export default function GroupAssignment({ tournament: t, teams, refresh }) {
  const numGroups = (t.type_config || {}).num_groups || 2;
  const qualifiers = (t.type_config || {}).qualifiers || 2;
  const labels = LABELS.slice(0, numGroups);
  const [cols, setCols] = useState({});

  useEffect(() => {
    const next = { _unassigned: [] };
    labels.forEach((l) => (next[l] = []));
    [...teams].sort((a, b) => (a.seed || 0) - (b.seed || 0)).forEach((tm) => {
      if (tm.group && next[tm.group]) next[tm.group].push(tm);
      else next._unassigned.push(tm);
    });
    setCols(next);
    // eslint-disable-next-line
  }, [teams, numGroups]);

  const onDragEnd = (res) => {
    const { source, destination } = res;
    if (!destination) return;
    const from = Array.from(cols[source.droppableId]);
    const [moved] = from.splice(source.index, 1);
    if (source.droppableId === destination.droppableId) {
      from.splice(destination.index, 0, moved);
      setCols({ ...cols, [source.droppableId]: from });
    } else {
      const to = Array.from(cols[destination.droppableId]);
      to.splice(destination.index, 0, moved);
      setCols({ ...cols, [source.droppableId]: from, [destination.droppableId]: to });
    }
  };

  const save = async () => {
    const payload = [];
    let seq = 1;
    labels.forEach((l) => cols[l].forEach((tm) => payload.push({ id: tm.id, seed: seq++, group: l })));
    cols._unassigned.forEach((tm) => payload.push({ id: tm.id, seed: seq++, group: null }));
    try {
      await api.put(`/tournaments/${t.id}/assignment`, { teams: payload });
      toast.success("Groups saved");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const randomize = () => {
    const all = labels.flatMap((l) => cols[l]).concat(cols._unassigned);
    const shuffled = [...all].sort(() => Math.random() - 0.5);
    const next = { _unassigned: [] };
    labels.forEach((l) => (next[l] = []));
    shuffled.forEach((tm, i) => next[labels[i % labels.length]].push(tm));
    setCols(next);
  };

  if (Object.keys(cols).length === 0) return null;

  return (
    <div className="space-y-4" data-testid="group-assignment">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Drag teams between groups · top <strong>{qualifiers}</strong> of each group qualify</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={randomize} data-testid="randomize-groups"><Shuffle className="mr-1.5 h-4 w-4" /> Randomize</Button>
          <Button size="sm" onClick={save} data-testid="save-groups"><Save className="mr-1.5 h-4 w-4" /> Save groups</Button>
        </div>
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {labels.map((l) => (
            <GroupColumn key={l} id={l} title={`Group ${l}`} teams={cols[l]} qualifiers={qualifiers} />
          ))}
          <GroupColumn id="_unassigned" title="Unassigned" teams={cols._unassigned} muted />
        </div>
      </DragDropContext>
    </div>
  );
}

function GroupColumn({ id, title, teams, qualifiers, muted }) {
  return (
    <Card className="p-3" data-testid={`group-col-${id}`}>
      <div className="mb-2 flex items-center gap-2 text-label text-muted-foreground"><Users2 className="h-3.5 w-3.5" /> {title} <span className="ml-auto">{teams.length}</span></div>
      <Droppable droppableId={id}>
        {(prov, snap) => (
          <div ref={prov.innerRef} {...prov.droppableProps}
            className={`min-h-[60px] space-y-1.5 rounded-lg p-1 transition-colors ${snap.isDraggingOver ? "bg-primary/5 outline-dashed outline-1 outline-primary/40" : ""}`}>
            {teams.map((tm, i) => (
              <Draggable key={tm.id} draggableId={tm.id} index={i}>
                {(p, s) => (
                  <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}
                    className={`flex items-center gap-2 rounded-lg border bg-card p-2 ${s.isDragging ? "dnd-dragging" : ""} ${!muted && qualifiers && i < qualifiers ? "border-primary/50" : ""}`}
                    data-testid={`group-team-${tm.id}`}>
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <TeamAvatar team={tm} size={22} />
                    <span className="truncate text-sm font-medium">{tm.name}</span>
                    {!muted && qualifiers && i < qualifiers && <span className="ml-auto rounded bg-primary/10 px-1.5 text-[10px] font-bold text-primary">Q</span>}
                  </div>
                )}
              </Draggable>
            ))}
            {prov.placeholder}
          </div>
        )}
      </Droppable>
    </Card>
  );
}
